/**
 * Usuarios (sección 3): tabla global con búsqueda. Acciones auditadas en el
 * backend: cambiar rol (con confirmación — es lo que hoy se hace por SQL) y
 * desactivar/reactivar. NO se crean usuarios (el registro es el flujo real).
 * El admin no puede cambiarse el rol ni desactivarse a sí mismo (el backend lo
 * rechaza; aquí además se deshabilitan las acciones sobre la propia fila).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/endpoints.js";
import { useAuth } from "../../auth/AuthContext.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import type { Rol, UsuarioAdmin } from "../../api/types.js";
import { Async, ConfirmDialog, RolBadge, fmtFechaCorta } from "./ui.js";
import s from "./admin.module.css";

const ROLES: Rol[] = ["padre", "terapeuta", "admin"];

type Pendiente =
  | { tipo: "rol"; user: UsuarioAdmin; nuevoRol: Rol }
  | { tipo: "activo"; user: UsuarioAdmin; activar: boolean };

export function AdminUsers() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const q = useQuery(() => adminApi.usuarios());
  const [busqueda, setBusqueda] = useState("");
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtrados = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    const lista = q.data ?? [];
    if (!t) return lista;
    return lista.filter(
      (u) => u.nombre.toLowerCase().includes(t) || u.email.toLowerCase().includes(t),
    );
  }, [q.data, busqueda]);

  async function ejecutar() {
    if (!pendiente) return;
    setOcupado(true);
    setError(null);
    try {
      if (pendiente.tipo === "rol") {
        await adminApi.cambiarRol(pendiente.user.id, pendiente.nuevoRol);
      } else if (pendiente.activar) {
        await adminApi.reactivar(pendiente.user.id);
      } else {
        await adminApi.desactivar(pendiente.user.id);
      }
      setPendiente(null);
      q.reload();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}
      <div className={s.toolbar}>
        <input
          className={s.search}
          placeholder="Buscar por nombre o email…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <span className={s.count}>{filtrados.length} usuarios</span>
      </div>

      <div className={s.panel}>
        <Async q={q}>
          {() => (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Creado</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((u) => {
                    const esYo = u.id === usuario?.id;
                    return (
                      <tr key={u.id}>
                        <td>
                          {u.nombre}
                          {esYo ? <span className={s.muted}> (tú)</span> : null}
                        </td>
                        <td className={s.muted}>{u.email}</td>
                        <td>
                          <RolBadge rol={u.rol} />
                        </td>
                        <td className={s.muted}>{fmtFechaCorta(u.createdAt)}</td>
                        <td>
                          {u.activo ? (
                            <span className={`${s.badge} ${s.badgeGreen}`}>Activa</span>
                          ) : (
                            <span className={`${s.badge} ${s.badgeRed}`}>Inactiva</span>
                          )}
                        </td>
                        <td>
                          <div className={s.btnRow}>
                            {u.rol === "terapeuta" ? (
                              <button
                                className={s.btn}
                                onClick={() => navigate(`/admin/creditos?terapeuta=${u.id}`)}
                              >
                                Créditos y ventas
                              </button>
                            ) : null}
                            <select
                              className={s.select}
                              value={u.rol}
                              disabled={esYo}
                              onChange={(e) =>
                                setPendiente({
                                  tipo: "rol",
                                  user: u,
                                  nuevoRol: e.target.value as Rol,
                                })
                              }
                            >
                              {ROLES.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                            {u.activo ? (
                              <button
                                className={`${s.btn} ${s.btnDanger}`}
                                disabled={esYo}
                                onClick={() => setPendiente({ tipo: "activo", user: u, activar: false })}
                              >
                                Desactivar
                              </button>
                            ) : (
                              <button
                                className={s.btn}
                                onClick={() => setPendiente({ tipo: "activo", user: u, activar: true })}
                              >
                                Reactivar
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtrados.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={s.empty}>
                        Sin usuarios que coincidan.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </Async>
      </div>

      {pendiente?.tipo === "rol" ? (
        <ConfirmDialog
          titulo="Cambiar rol"
          cuerpo={
            <>
              ¿Cambiar el rol de <b>{pendiente.user.nombre}</b> de <b>{pendiente.user.rol}</b> a{" "}
              <b>{pendiente.nuevoRol}</b>? Queda registrado en la auditoría.
            </>
          }
          confirmar="Cambiar rol"
          ocupado={ocupado}
          onConfirm={() => void ejecutar()}
          onCancel={() => setPendiente(null)}
        />
      ) : null}
      {pendiente?.tipo === "activo" ? (
        <ConfirmDialog
          titulo={pendiente.activar ? "Reactivar cuenta" : "Desactivar cuenta"}
          cuerpo={
            pendiente.activar ? (
              <>
                ¿Reactivar la cuenta de <b>{pendiente.user.nombre}</b>? Podrá iniciar sesión de nuevo.
              </>
            ) : (
              <>
                ¿Desactivar la cuenta de <b>{pendiente.user.nombre}</b>? No podrá iniciar sesión (es
                reversible; no borra datos).
              </>
            )
          }
          confirmar={pendiente.activar ? "Reactivar" : "Desactivar"}
          peligro={!pendiente.activar}
          ocupado={ocupado}
          onConfirm={() => void ejecutar()}
          onCancel={() => setPendiente(null)}
        />
      ) : null}
    </>
  );
}
